import React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { List } from '@oliasoft-open-source/react-ui-library';
import { v4 as uuidv4 } from 'uuid';
import translations from 'src/internationalisation/translation-map.json';
import { settingEntity } from 'src/enums/settings';
import {
  templateSelected,
  addInitialTemplate,
  duplicateTemplate,
  reorderTemplate,
} from 'store/entities/templates/templates';
import { selectUserTemplates } from 'src/store/entities/templates/selectors';
import { convertToKebabCase } from 'src/common/utils/string-utils';
import { downloadTemplate, uploadTemplate } from './templates-import-export';

const TemplatesList = ({
  entity,
  templates,
  isLoading,
  userTemplates,
  onClickDelete,
  onShowCopyModal,
  onClickUpload,
  templateSelected,
  addInitialTemplate,
  duplicateTemplate,
  saveTemplates,
  reorderTemplate,
}) => {
  const { t } = useTranslation();
  const { company: companyId } = useParams();
  const onClickItem = (templateId) => templateSelected({ templateId, entity });

  const onCopyToUserSettings = (index) => {
    const copied = {
      ...templates[index],
      templateId: uuidv4(),
    };
    const updatedTemplates = [...userTemplates, copied];
    saveTemplates(updatedTemplates, settingEntity.USER);
  };

  const templatesList =
    templates?.map((item, index) => ({
      id: index,
      templateId: item.templateId,
      name: item.highLevel.name,
      active: item.active,
      testId: `automation-rulesets-list-${convertToKebabCase(item.highLevel.name)}-item`,
      actions: [
        {
          label: 'More',
          testId: `automation-rulesets-list-${convertToKebabCase(item.highLevel.name)}-more-button`,
          disabled: isLoading,
          subActions: [
            {
              icon: 'delete',
              label: 'Delete',
              testId: `automation-rulesets-list-${convertToKebabCase(item.highLevel.name)}-delete-button`,
            },
            {
              icon: 'clone',
              label: 'Duplicate',
              testId: `automation-rulesets-list-${convertToKebabCase(item.highLevel.name)}-duplicate-button`,
            },
            entity === settingEntity.COMPANY && {
              icon: 'copy',
              label: 'Copy to user settings',
              testId: `automation-rulesets-list-${convertToKebabCase(item.highLevel.name)}-copy-button`,
            },
            {
              icon: 'download',
              label: 'Download',
              testId: `automation-rulesets-list-${convertToKebabCase(item.highLevel.name)}-download-button`,
            },
          ].filter(Boolean),
        },
      ],
    })) || [];

  return (
    <div>
      <List
        testId="automation-rulesets-list"
        list={{
          name: t(translations.automationRulesets),
          actions:
            entity === settingEntity.COMPANY
              ? [
                  {
                    label: 'More',
                    testId: 'automation-rulesets-list-subheading-more-button',
                    subActions: [
                      {
                        label: 'Add',
                        icon: 'add',
                        testId: 'automation-rulesets-list-subheading-add-button',
                        onClick: () =>
                          addInitialTemplate(t(translations.settings_newRuleset), templates, entity, companyId),
                        disabled: isLoading,
                      },
                      {
                        label: t(translations.uploadRuleset),
                        icon: 'upload',
                        testId: 'automation-rulesets-list-subheading-upload-button',
                        onClick: () => uploadTemplate((content) => onClickUpload(content, companyId)),
                        disabled: isLoading,
                      },
                    ],
                  },
                ]
              : [
                  {
                    label: 'More',
                    testId: 'automation-rulesets-list-subheading-more-button',
                    subActions: [
                      {
                        label: 'Add',
                        icon: 'add',
                        testId: 'automation-rulesets-list-subheading-add-button',
                        onClick: () =>
                          addInitialTemplate(t(translations.settings_newRuleset), templates, entity, companyId),
                        disabled: isLoading,
                      },
                      {
                        icon: 'copy',
                        label: t(translations.settings_importFromCompanySettings),
                        testId: 'automation-rulesets-list-subheading-import-button',
                        onClick: onShowCopyModal,
                      },
                      {
                        label: t(translations.uploadRuleset),
                        icon: 'upload',
                        testId: 'automation-rulesets-list-subheading-upload-button',
                        onClick: () => uploadTemplate(onClickUpload, companyId),
                        disabled: isLoading,
                      },
                    ],
                  },
                ],
          items: templatesList?.map((template, i) => ({
            ...template,
            onClick: () => onClickItem(template.templateId),
            actions: template.actions.map((action) => ({
              ...action,
              subActions: action.subActions.map((subAction) => ({
                ...subAction,
                onClick: () => {
                  if (subAction.icon === 'delete') {
                    onClickDelete(template.templateId);
                  }
                  if (subAction.icon === 'clone') {
                    duplicateTemplate(i, templates, entity, companyId);
                  }
                  if (subAction.icon === 'copy') {
                    onCopyToUserSettings(i);
                  }
                  if (subAction.icon === 'download') {
                    downloadTemplate(templates[i]);
                  }
                },
              })),
            })),
          })),
        }}
        draggable
        onListReorder={({ from, to }) => reorderTemplate(from, to, templates, entity, companyId)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userTemplates: selectUserTemplates(state),
});
const mapDispatchToProps = {
  templateSelected,
  addInitialTemplate,
  duplicateTemplate,
  reorderTemplate,
};

const Container = connect(mapStateToProps, mapDispatchToProps)(TemplatesList);

export { Container as TemplatesList };
